/**
 * Formatting for Warehouse and Invoicing lists and records. Site-local calendar days arrive from
 * the server as `YYYY-MM-DD` and are printed without a time-zone shift.
 */

const dayFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const dayWithYearFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});
const monthYearFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
const weekdayFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});
const moneyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

function parseDay(day: string | null | undefined): Date | null {
  if (!day) return null;
  const parsed = new Date(`${day.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** "Oct 7"; adds the year when it is not the current one. */
export function formatDay(day: string | null | undefined, currentYear = new Date().getFullYear()): string | null {
  const date = parseDay(day);
  if (!date) return null;
  return date.getUTCFullYear() === currentYear ? dayFormatter.format(date) : dayWithYearFormatter.format(date);
}

/** "Dec 2014", for how old a backlog reaches. */
export function formatMonthYear(day: string | null | undefined): string | null {
  const date = parseDay(day);
  return date ? monthYearFormatter.format(date) : null;
}

/** "Wed Oct 7". */
export function formatWeekday(day: string | null | undefined): string | null {
  const date = parseDay(day);
  return date ? weekdayFormatter.format(date) : null;
}

/** "Oct 7 → Oct 9", "Oct 7 → Oct 7", or one side when the other is missing. */
export function formatDayRange(from: string | null | undefined, to: string | null | undefined): string | null {
  const start = formatDay(from);
  const end = formatDay(to);
  if (start && end) return `${start} → ${end}`;
  return start ?? end;
}

/** "Oct 8 – Oct 14" for group headers. */
export function formatDaySpan(from: string | null | undefined, to: string | null | undefined): string | null {
  const start = formatDay(from);
  const end = formatDay(to);
  if (start && end && start !== end) return `${start} – ${end}`;
  return start ?? end;
}

/** A timestamp as a date and time, "Oct 6, 4:10 PM". */
export function formatDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "just now", "3h ago", "Yesterday", "4 days ago", then the date. */
export function formatRelativeTime(value: string | null | undefined, now: Date = new Date()): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const minutes = Math.round((now.getTime() - date.getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

export function formatMoney(amount: number | null | undefined): string | null {
  if (amount == null || !Number.isFinite(amount)) return null;
  return moneyFormatter.format(amount);
}

export function formatCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '0';
  return numberFormatter.format(value);
}

/** "1 line", "3 lines". */
export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${formatCount(count)} ${count === 1 ? singular : plural}`;
}
